import React, { useState, useEffect, useRef } from 'react';
import { Dropdown } from 'semantic-ui-react';
import posthog from 'posthog-js';
import { axiosBasic } from '../../../../../api/axios';
import { config } from '../../../../../api/endPoints';
import { convertToAlpha2 } from '../../../../../utils/countryCodeMapping';

const PublicHolidaysAPI = config.REST_API.PublicHolidays;

const CountrySelector = ({
    onCountryChange,
    selectedCountry,
    placeholder = "Select Country",
    fluid = true,
    className = "",
    showClearButton = false,
    onCountriesLoaded = null
}) => {
    const [countries, setCountries] = useState([]);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);
    const [defaultSelected, setDefaultSelected] = useState(null);
    const fetchDoneRef = useRef(false);

    useEffect(() => {
        if (fetchDoneRef.current) return;
        fetchDoneRef.current = true;
        fetchCountries();
    }, []);

    const fetchCountries = async () => {
        setLoading(true);
        setError(null);

        try {
            const response = await axiosBasic.get(PublicHolidaysAPI.GetOfficesWithCountries);

            if (response.data && response.data.success) {
                const countryOptions = response.data.data.map((item, index) => {
                    // Get the country code from flag or countryCode
                    let countryCode = item.flag || item.countryCode;

                    // Convert to 2-letter code if needed
                    if (countryCode) {
                        // If it's already 2 letters, use it as is (lowercase)
                        if (countryCode.length === 2) {
                            countryCode = countryCode.toLowerCase();
                        }
                        // If it's 3 letters, convert it
                        else if (countryCode.length === 3) {
                            countryCode = convertToAlpha2(countryCode) || countryCode.toLowerCase();
                        }
                        // If it's something else, try to convert or use as is
                        else {
                            countryCode = convertToAlpha2(countryCode) || countryCode.toLowerCase();
                        }
                    }

                    return {
                        key: item.id || index,
                        value: item.countryName || item.name,
                        text: item.countryName || item.name,
                        flag: countryCode, // Use converted 2-letter code for Semantic UI
                        countryId: item.countryId,
                        officeId: item.officeId,
                        officeName: item.officeName
                    };
                });

                setCountries(countryOptions);
                // Set first country as default if no country is selected
                if (!selectedCountry && countryOptions.length > 0) {
                    setDefaultSelected(countryOptions[0].value);
                    onCountryChange({
                        countryName: countryOptions[0].value,
                        countryId: countryOptions[0].countryId,
                        officeId: countryOptions[0].officeId,
                        officeName: countryOptions[0].officeName,
                        flag: countryOptions[0].flag
                    });
                }

                // Notify parent component about loaded countries
                if (onCountriesLoaded) {
                    onCountriesLoaded(countryOptions.length, countryOptions);
                }
            }
        } catch (err) {
            if (typeof posthog !== "undefined" && posthog.capture) {
                posthog.capture("countrySelectorFetchCountries", {
                    type: "exception",
                    success: false,
                    reason: err?.response?.data?.message ?? err?.message ?? "Request failed",
                    status: err?.response?.status,
                });
            }
        } finally {
            setLoading(false);
        }
    };

    const handleCountryChange = (event, { value }) => {
        // Handle clearing the selection
        if (!value) {
            onCountryChange({
                countryName: null,
                countryId: null,
                officeId: null,
                officeName: null,
                flag: null
            });
            return;
        }

        const selectedOption = countries.find(country => country.value === value);
        onCountryChange({
            countryName: value,
            countryId: selectedOption?.countryId,
            officeId: selectedOption?.officeId,
            officeName: selectedOption?.officeName,
            flag: selectedOption?.flag
        });
    };

    if (error) {
        return (
            <div className="ui message error">
                <div className="header">Error</div>
                <p>Failed to load countries</p>
            </div>
        );
    }

    return (
        <Dropdown
            placeholder={placeholder}
            fluid={fluid}
            selection
            clearable={showClearButton}
            loading={loading}
            options={countries}
            value={selectedCountry || defaultSelected}
            onChange={handleCountryChange}
            className={`simple-country-dropdown ${className}`}
            noResultsMessage="No countries found"
            scrolling={true}
            maxHeight={300}
        />
    );
};

export default CountrySelector;